import type { AlertColor } from '@mui/material';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { Card } from '../configurations/types';

type CardAlert = {
  type: AlertColor;
  message: ReactNode | string;
  key: string;
  dismissable?: boolean;
  index?: number;
};

interface CoverSheetCardContextInterface {
  card: Card;
  rhfKey: string;
  index: number;
  appendNewCard: (data: Partial<unknown> | Partial<unknown>[]) => void;
  alerts: CardAlert[];
  addAlert: (alert: CardAlert) => void;
  clearAlerts: () => void;
  clearAlert: (key: string) => void;
}

const CoverSheetCardContext = createContext<CoverSheetCardContextInterface>({
  card: {} as Card,
  rhfKey: '',
  index: 0,
  appendNewCard: () => null,
  alerts: [],
  addAlert: () => '',
  clearAlerts: () => null,
  clearAlert: () => null,
});

export function useCoverSheetCardContext() {
  return useContext(CoverSheetCardContext);
}

interface Props {
  children: ReactNode;
  card: Card;
  rhfKey: string;
  index: number;
  appendNewCard: (data: Partial<unknown> | Partial<unknown>[]) => void;
}

export const CoverSheetCardProvider = ({
  children,
  rhfKey,
  card,
  index,
  appendNewCard,
}: Props) => {
  const [alerts, setAlerts] = useState<CardAlert[]>([]);

  const addAlert = useCallback(
    (alert: CardAlert) => {
      setAlerts((prev) => {
        if (!prev.find(({ key }) => key === alert.key)) return [...prev, alert];
        return prev;
      });
    },
    [setAlerts],
  );

  const clearAlerts = useCallback(() => setAlerts([]), []);

  const clearAlert = useCallback(
    (key: string) =>
      setAlerts((prev) => prev.filter((alert) => alert.key !== key)),
    [setAlerts],
  );

  const contextValue = useMemo<CoverSheetCardContextInterface>(
    () => ({
      card,
      rhfKey,
      index,
      appendNewCard,
      alerts,
      addAlert,
      clearAlerts,
      clearAlert,
    }),
    [
      card,
      rhfKey,
      index,
      appendNewCard,
      alerts,
      addAlert,
      clearAlerts,
      clearAlert,
    ],
  );

  return (
    <CoverSheetCardContext.Provider value={contextValue}>
      {children}
    </CoverSheetCardContext.Provider>
  );
};
